import type { EnviarMensajeInput, InstanciaPublic } from '@app/shared';
import { Mic, SendHorizontal, Square, Trash2, X } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';

import { ChipInstancia } from './Chips';

interface AudioMeta {
  instancia?: string;
  fijarComoPreferida?: boolean;
}

interface CompositorProps {
  /** Texto inicial (en el Ciclo 2 será la sugerencia del LLM). */
  valorInicial?: string | undefined;
  /** Los números activos por los que se puede escribir (Ciclo 1b, R3). */
  instancias: InstanciaPublic[];
  /** El número por defecto de esta persona: siempre se le responde por este. */
  preferida: string;
  /** Cambia con el lead: reinicia el selector al default. */
  leadId: string;
  enviando: boolean;
  enviandoAudio: boolean;
  deshabilitado?: boolean;
  aviso?: string | null;
  onEnviar: (input: EnviarMensajeInput) => Promise<void>;
  /** Ciclo audio, R3: manda el audio grabado. */
  onEnviarAudio: (blob: Blob, meta: AudioMeta) => Promise<void>;
}

type EstadoAudio = 'inactivo' | 'grabando' | 'preview';

/** Lo que el padre puede pedirle al compositor desde afuera (el botón "usar esta sugerencia"). */
export interface CompositorHandle {
  /** Reemplaza el borrador actual por este texto — acción explícita, nunca automática. */
  fijarTexto: (texto: string) => void;
}

// Barra de respuesta: Enter envía, Shift+Enter hace salto de línea. Crece con el
// texto hasta ~8 líneas. Se vacía solo cuando el envío salió bien.
//
// "Enviar desde" arranca SIEMPRE en el número por defecto de la persona; elegir
// el otro es un acto deliberado (aparece un aviso y la casilla para fijarlo).
export const Compositor = forwardRef<CompositorHandle, CompositorProps>(function Compositor(p, handleRef) {
  const es = useI18n();
  const [texto, setTexto] = useState(p.valorInicial ?? '');
  const [instancia, setInstancia] = useState(p.preferida);
  const [fijar, setFijar] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  // true si Ailén ya tocó el borrador de ESTA conversación (a mano o aplicando una
  // sugerencia): mientras sea true, una sugerencia nueva que llega sola (por SSE) no
  // pisa lo que está escribiendo — se avisa aparte y se aplica a pedido (bug real
  // reportado por Ailén, 01/10/2026: el sugeridor le escribía encima mientras tipeaba).
  const tocadoRef = useRef(false);
  const leadAnteriorRef = useRef(p.leadId);

  const [estadoAudio, setEstadoAudio] = useState<EstadoAudio>('inactivo');
  const [blobAudio, setBlobAudio] = useState<Blob | null>(null);
  const [errorAudio, setErrorAudio] = useState<string | null>(null);
  const [segundosGrabados, setSegundosGrabados] = useState(0);
  const grabadorRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // true mientras se cancela: el handler onstop del grabador no arma la preview.
  const cancelandoRef = useRef(false);
  const intervaloRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const detenerCronometro = (): void => {
    if (intervaloRef.current !== null) {
      clearInterval(intervaloRef.current);
      intervaloRef.current = null;
    }
  };
  useEffect(() => detenerCronometro, []);

  useEffect(() => {
    const conversacionNueva = leadAnteriorRef.current !== p.leadId;
    leadAnteriorRef.current = p.leadId;
    if (conversacionNueva) {
      // Lead distinto: SIEMPRE se repinta (incluida una sugerencia vacía), es
      // una conversación nueva y nada de lo anterior aplica acá.
      tocadoRef.current = false;
      setTexto(p.valorInicial ?? '');
      return;
    }
    if (tocadoRef.current) return; // Ailén ya está escribiendo lo suyo: no se toca.
    setTexto(p.valorInicial ?? '');
  }, [p.leadId, p.valorInicial]);

  useImperativeHandle(handleRef, () => ({
    fijarTexto: (t: string) => {
      tocadoRef.current = false;
      setTexto(t);
      ref.current?.focus();
    },
  }));

  // Al cambiar de persona (o si cambia su default), el selector vuelve al default
  // y se corta cualquier grabación en curso (nunca se manda un audio al lead equivocado).
  useEffect(() => {
    setInstancia(p.preferida);
    setFijar(false);
    cancelandoRef.current = true;
    grabadorRef.current?.stop();
    detenerCronometro();
    setEstadoAudio('inactivo');
    setBlobAudio(null);
    setErrorAudio(null);
  }, [p.leadId, p.preferida]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${String(Math.min(el.scrollHeight, 200))}px`;
  }, [texto]);

  const previewUrl = useMemo(() => (blobAudio ? URL.createObjectURL(blobAudio) : null), [blobAudio]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const otroNumero = instancia !== p.preferida;
  const puedeEnviar = texto.trim().length > 0 && !p.enviando && !p.deshabilitado;
  const metaAudio: AudioMeta = otroNumero ? { instancia, fijarComoPreferida: fijar } : {};

  const enviar = async (): Promise<void> => {
    if (!puedeEnviar) return;
    const t = texto.trim();
    await p.onEnviar({
      texto: t,
      ...(otroNumero ? { instancia, fijarComoPreferida: fijar } : {}),
    });
    setTexto('');
    tocadoRef.current = false; // borrador limpio: la próxima sugerencia puede precargar de nuevo
    ref.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void enviar();
    }
  };

  const iniciarGrabacion = async (): Promise<void> => {
    setErrorAudio(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      cancelandoRef.current = false;
      const grabador = new MediaRecorder(stream);
      grabador.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      grabador.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        detenerCronometro();
        if (cancelandoRef.current) {
          setEstadoAudio('inactivo');
          return;
        }
        setBlobAudio(new Blob(chunksRef.current, { type: grabador.mimeType || 'audio/webm' }));
        setEstadoAudio('preview');
      };
      grabadorRef.current = grabador;
      grabador.start();
      setEstadoAudio('grabando');
      setSegundosGrabados(0);
      intervaloRef.current = setInterval(() => {
        setSegundosGrabados((s) => s + 1);
      }, 1000);
    } catch {
      setErrorAudio(es.bandeja.audio.permisoDenegado);
    }
  };

  const cancelarGrabacion = (): void => {
    cancelandoRef.current = true;
    grabadorRef.current?.stop();
  };

  const descartarAudio = (): void => {
    setBlobAudio(null);
    setEstadoAudio('inactivo');
  };

  const formatoDuracion = (s: number): string =>
    `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;

  const enviarAudio = async (): Promise<void> => {
    if (!blobAudio || p.enviandoAudio) return;
    await p.onEnviarAudio(blobAudio, metaAudio);
    setBlobAudio(null);
    setEstadoAudio('inactivo');
  };

  const elegida = p.instancias.find((i) => i.nombre === instancia);

  return (
    <div className="border-t border-border-subtle bg-surface p-3">
      {p.aviso && <p className="mb-2 text-xs text-warning">{p.aviso}</p>}
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-text-subtle">
        <label className="flex items-center gap-1.5">
          <span>{es.bandeja.numeros.enviarDesde}</span>
          <select
            value={instancia}
            onChange={(e) => {
              setInstancia(e.target.value);
              setFijar(false);
            }}
            aria-label={es.bandeja.numeros.enviarDesde}
            className={cn(
              'h-7 rounded-md border bg-surface px-2 text-xs text-text-strong',
              otroNumero ? 'border-warning' : 'border-border-strong',
            )}
          >
            {p.instancias.map((i) => (
              <option key={i.nombre} value={i.nombre}>
                {i.etiqueta} · {i.nombre}
                {i.nombre === p.preferida ? ' ★' : ''}
              </option>
            ))}
          </select>
        </label>
        {elegida && <ChipInstancia instancia={elegida} preferida={!otroNumero} />}
        {otroNumero && (
          <>
            <span className="text-warning">{es.bandeja.numeros.cambioAviso}</span>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={fijar}
                onChange={(e) => setFijar(e.target.checked)}
                className="h-3.5 w-3.5 accent-marca-primario"
              />
              {es.bandeja.numeros.fijarPreferido}
            </label>
          </>
        )}
      </div>
      {errorAudio && <p className="mb-2 text-xs text-danger">{errorAudio}</p>}
      {estadoAudio === 'grabando' ? (
        <div className="flex items-center gap-2">
          <span className="flex flex-1 items-center gap-2 text-sm text-text-strong">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-danger" aria-hidden />
            {es.bandeja.audio.grabando}
            <span className="tabular-nums text-text-subtle">{formatoDuracion(segundosGrabados)}</span>
          </span>
          <Button
            type="button"
            variant="outline"
            onClick={cancelarGrabacion}
            aria-label={es.bandeja.audio.cancelar}
            className="h-10 shrink-0"
          >
            <X size={16} aria-hidden />
          </Button>
          <Button
            type="button"
            onClick={() => grabadorRef.current?.stop()}
            aria-label={es.bandeja.audio.detener}
            className="h-10 shrink-0"
          >
            <Square size={16} aria-hidden />
          </Button>
        </div>
      ) : estadoAudio === 'preview' && previewUrl ? (
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- previsualización de la nota de voz recién grabada */}
          <audio controls src={previewUrl} className="h-9 flex-1" />
          <Button
            type="button"
            variant="outline"
            onClick={descartarAudio}
            disabled={p.enviandoAudio}
            aria-label={es.bandeja.audio.descartar}
            className="h-10 shrink-0"
          >
            <Trash2 size={16} aria-hidden />
          </Button>
          <Button
            type="button"
            onClick={() => void enviarAudio()}
            disabled={p.enviandoAudio}
            aria-label={es.bandeja.audio.enviar}
            className="h-10 shrink-0"
          >
            <SendHorizontal size={16} aria-hidden />
            <span className="hidden sm:inline">
              {p.enviandoAudio ? es.bandeja.audio.enviando : es.bandeja.audio.enviar}
            </span>
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <Textarea
            ref={ref}
            value={texto}
            onChange={(e) => {
              tocadoRef.current = true;
              setTexto(e.target.value);
            }}
            onKeyDown={onKeyDown}
            placeholder={es.bandeja.compositor.placeholder}
            aria-label={es.bandeja.compositor.placeholder}
            rows={1}
            disabled={p.deshabilitado}
            className={cn('max-h-[200px] min-h-[40px] flex-1 resize-none py-2 text-sm')}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => void iniciarGrabacion()}
            disabled={p.deshabilitado}
            aria-label={es.bandeja.audio.grabar}
            className="h-10 shrink-0"
          >
            <Mic size={16} aria-hidden />
          </Button>
          <Button
            type="button"
            onClick={() => void enviar()}
            disabled={!puedeEnviar}
            aria-label={es.bandeja.compositor.enviar}
            className="h-10 shrink-0"
          >
            <SendHorizontal size={16} aria-hidden />
            <span className="hidden sm:inline">
              {p.enviando ? es.bandeja.compositor.enviando : es.bandeja.compositor.enviar}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
});
